import type { IChecklistTask } from "@features/onboarding";
import { MediaPanelTabEnum } from "@pages/editor/modules/media-lib/types";
import { openTeamDialog } from "@/shared/utils/open-team-dialog";
import type { NavigateFunction } from "react-router-dom";
import {
  IconApps,
  IconUserCog,
  IconVariable,
  IconVariablePlus,
  IconBrain,
  IconBrandLinkedin,
  IconBrandYoutube,
  IconChartBar,
  IconCircleCheck,
  IconCode,
  IconFolders,
  IconListDetails,
  IconPhotoVideo,
  IconShare,
  IconSignRight,
  IconStar,
  IconThumbUp,
  IconTools,
  IconUserPlus,
  IconUserScreen,
  IconUsersGroup,
  IconWorld,
} from "@tabler/icons-react";

export type TPlatformChecklistGroup =
  | "quick-wins"
  | "activate"
  | "pro"
  | "spread";

export interface IPlatformTask extends IChecklistTask<TPlatformChecklistGroup> {
  /** Bonus credits, shown as a pill. Tasks without one render no pill. */
  credits?: number;
  /** Label for the primary action shown on row hover. */
  action?: string;
  /** Where the primary action goes. An absolute http(s) URL opens in a new tab and completes the task. */
  href?: string;
  /** Relaunches the platform tour instead of navigating to `href`. */
  startsTour?: boolean;
  /** Runs instead of navigating to `href`: a dialog, or a page opened on a particular tab. */
  run?: (navigate: NavigateFunction) => void;
  /** Help Center article behind the row's Learn more button; rows without one show no button. */
  learnMoreUrl?: string;
  /** Completed from HubSpot when marketing marks it on the contact, never by opening the link. */
  verifiedByHubspot?: boolean;
}

export const PLATFORM_CHECKLIST_GROUPS: {
  key: TPlatformChecklistGroup;
  title: string;
  hint: string;
}[] = [
  { key: "quick-wins", title: "Quick wins", hint: "Get set up in minutes" },
  {
    key: "activate",
    title: "Activate key features",
    hint: "Unlock what Storydoc can do",
  },
  { key: "pro", title: "Become a Storydoc Pro", hint: "Level up your skills" },
  {
    key: "spread",
    title: "Spread the word",
    hint: "Help others discover Storydoc",
  },
];

export const PLATFORM_CHECKLIST_TASKS: IPlatformTask[] = [
  {
    key: "finish-platform-tour",
    title: "Finish the platform tour",
    description: "Get a quick walkthrough of your workspace",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/15483013-how-to-navigate-the-storydoc-workspace",
    icon: IconSignRight,
    group: "quick-wins",
    credits: 50,
    action: "Start tour",
    startsTour: true,
  },
  {
    key: "complete-profile",
    title: "Complete your profile",
    description: "Add role, company, profile picture",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/11987932-how-to-set-up-your-profile-in-storydoc",
    icon: IconCircleCheck,
    group: "quick-wins",
    credits: 80,
    action: "Complete",
    href: "/pages/settings",
  },
  {
    key: "share-first-storydoc",
    title: "Share your first Storydoc",
    description: "Send a tracked link to someone",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/12441983-understanding-your-sharing-options",
    icon: IconShare,
    group: "quick-wins",
    credits: 100,
    action: "Share",
    href: "/pages/editor",
  },
  {
    key: "invite-colleague",
    title: "Invite a colleague",
    description: "Add a teammate to your workspace",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/11998891-inviting-team-members-and-assigning-roles",
    icon: IconUserPlus,
    group: "quick-wins",
    credits: 80,
    action: "Invite",
    href: "/pages/account",
  },
  {
    key: "view-first-analytics",
    title: "View your first analytics",
    description: "See who opened your deck and how they engaged",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/15120648-how-to-use-storydoc-analytics",
    icon: IconChartBar,
    group: "activate",
    credits: 50,
    action: "View",
    href: "/pages/analytics",
  },
  {
    key: "add-version-variable",
    title: "Add your first version variable",
    description: "Add variable to your deck",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/17212930-how-to-personalize-your-stories-with-variables",
    icon: IconVariable,
    group: "activate",
    credits: 120,
  },
  {
    key: "create-personalized-version",
    title: "Create your first personalized version",
    description: "Tailor a deck to a specific recipient",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/15691747-how-to-create-manage-and-update-my-versions",
    icon: IconListDetails,
    group: "activate",
    credits: 100,
  },
  {
    key: "organize-with-folders",
    title: "Organize with folders",
    description: "Group your stories by client, team, or project",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/12030274-managing-folders-create-organize-and-control-access",
    icon: IconFolders,
    group: "activate",
    action: "Go to stories",
    href: "/pages/editor",
  },
  {
    key: "set-up-brand",
    title: "Set up your brand",
    description: "Add your logo, fonts, and colors once and reuse everywhere",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/15181046-how-to-set-up-your-brand-in-storydoc",
    // The design uses the folders glyph here too — kept as drawn.
    icon: IconFolders,
    group: "activate",
    action: "Set up",
    href: "/pages/design",
  },
  {
    key: "define-ai-storybrain",
    title: "Define your AI Storybrain",
    description: "Teach AI your tone, audience, and messaging",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/15820856-how-to-use-ai-in-storydoc",
    icon: IconBrain,
    group: "activate",
    action: "Set up",
    run: (navigate) =>
      navigate("/pages/account", { state: { tab: "ai-brain" } }),
  },
  {
    key: "create-team-variable",
    title: "Create your first custom team variable",
    description: "Add custom variables to your decks",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/17212930-how-to-personalize-your-stories-with-variables",
    icon: IconVariablePlus,
    group: "activate",
    action: "Add variable",
    run: (navigate) =>
      navigate("/pages/variables", { state: { tab: "custom" } }),
  },
  {
    key: "set-up-profile-variables",
    title: "Set up your Teams profile variables",
    description: "Add profile variables, to re-use them later in your decks",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/11987932-how-to-set-up-your-profile-in-storydoc",
    icon: IconUserCog,
    group: "activate",
    action: "Set up",
    run: (navigate) =>
      navigate("/pages/variables", { state: { tab: "profile" } }),
  },
  {
    key: "set-up-media-library",
    title: "Set up your Media library",
    description: "Upload reusable assets for every deck",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/11999228-managing-your-media-library-creating-organizing-and-access-control",
    icon: IconPhotoVideo,
    group: "activate",
    action: "Upload",
    // Loaded on click: this file is in the sidebar's chunk on every route, the media library is not.
    run: () =>
      void import("@pages/editor/modules/media-lib/utils/open-media-lib-dialog").then(
        ({ openMediaLibDialog }) =>
          openMediaLibDialog({
            source: "settings",
            category: MediaPanelTabEnum.MediaLib,
          }),
      ),
  },
  {
    key: "team-plan-walkthrough",
    title: "Get a Team Plan walkthrough",
    description: "See how Storydoc scales across your team",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/12439060-post-install-checklist-for-team-admins",
    icon: IconUsersGroup,
    group: "activate",
    action: "Book a demo",
    run: () => openTeamDialog("onboarding hub"),
  },
  {
    key: "connect-integration",
    title: "Connect your first integration",
    description: "Sync your CRM, calendar, or marketing tools",
    learnMoreUrl:
      "https://help.storydoc.com/en/collections/14076251-integrations",
    icon: IconApps,
    group: "activate",
    credits: 100,
    action: "Connect",
    href: "/pages/profile/integrations",
  },
  {
    key: "first-api-call",
    title: "Make your first API call",
    description: "Automate deck creation from your own stack",
    learnMoreUrl: "https://help.storydoc.com/en/articles/13628226-storydoc-api",
    icon: IconCode,
    group: "activate",
    credits: 150,
    action: "Use API",
    href: "/pages/automations",
  },
  {
    key: "create-subdomain",
    title: "Create your subdomain",
    description: "Get a branded yourcompany.storydoc.live URL",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/11998743-how-to-set-up-a-custom-subdomain-branded-or-whitelabel",
    icon: IconWorld,
    group: "activate",
    action: "Create",
    href: "/pages/profile/add-ons",
  },
  {
    key: "custom-domain",
    title: "Connect a custom domain",
    description: "Host decks on your own domain",
    learnMoreUrl:
      "https://help.storydoc.com/en/articles/11998743-how-to-set-up-a-custom-subdomain-branded-or-whitelabel",
    icon: IconWorld,
    group: "activate",
    action: "Connect",
    href: "/pages/profile/add-ons",
  },
  {
    key: "webinar-library",
    title: "Review our webinar library",
    description: "Watch expert sessions on high-converting decks",
    icon: IconUserScreen,
    group: "pro",
    action: "Watch",
    href: "https://www.storydoc.com/resources/webinars",
  },
  {
    key: "design-workshop",
    title: "Join a design tips and tricks workshop",
    description: "Live session on making decks that stand out",
    icon: IconTools,
    group: "pro",
    action: "Book",
    href: "https://www.storydoc.com/resources/webinars",
  },
  {
    key: "follow-linkedin",
    title: "Follow us on linkedin",
    description: "Get tips, updates, and inspiration in your feed",
    icon: IconBrandLinkedin,
    group: "pro",
    credits: 50,
    action: "Follow",
    href: "https://www.linkedin.com/company/storydoc",
  },
  {
    key: "youtube-videos",
    title: "Watch our YouTube how-to videos",
    description: "Quick tutorials for every Storydoc feature",
    icon: IconBrandYoutube,
    group: "pro",
    action: "Watch",
    href: "https://www.youtube.com/@storydoc",
  },
  {
    key: "review-trustpilot",
    title: "Leave a Trustpilot or Capterra review",
    description: "Share your experience in a quick review",
    verifiedByHubspot: true,
    icon: IconStar,
    group: "spread",
    credits: 100,
    action: "Review",
    href: "https://www.trustpilot.com/evaluate/storydoc.com",
  },
  {
    key: "review-g2",
    title: "Leave a G2 review",
    description: "Tell other teams what you think",
    verifiedByHubspot: true,
    icon: IconStar,
    group: "spread",
    credits: 150,
    action: "Review",
    href: "https://www.g2.com/products/storydoc/reviews",
  },
  {
    key: "share-social",
    title: "Share Storydoc on LinkedIn or X",
    description: "Post about a deck you're proud of",
    icon: IconThumbUp,
    group: "spread",
    credits: 50,
    action: "Share",
    href: "https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fwww.storydoc.com",
  },
];
